import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api'
import { CloseIcon, ExpandIcon, PauseFillIcon, PlayFillIcon } from '../icons'
import { Spinner } from '../ui'
import type { Angle, Media, Take } from '../types'
import { clock, cls, takeTc } from '../util'
import type { PlayheadStore } from './store'
import { proxyLine, type ProxyProgress } from './Processing'
import { LookCanvas } from './LookCanvas'
import { LookMenu, type LookChoice } from './LookMenu'
import { DownloadMenu } from './DownloadMenu'

/*
  Every camera of a take, side by side by default, locked to one clock.
  The first rolling camera is the master (its audio plays); the others are
  muted and eased back into sync (a touch fast or slow) when they drift; only a
  drift over a second gets a hard seek, since a seek is a visible skip.
  A camera can roll several clips in one take, so each slot picks whichever
  of its clips covers the current moment.
*/
export type CamApi = {
  cue: (takeId: string, t: number) => void
  play: (takeId: string, t: number) => void
  toggle: () => void
  pause: () => void
  nudge: (dt: number) => void
  playRange: (takeId: string, start: number, end: number) => void
  /** Lines back to back (a chapter, the whole story). */
  playQueue: (list: { takeId: string; start: number; end: number }[]) => void
  current: () => { takeId: string | null; time: number; playing: boolean }
}

type Play = { status: string; url?: string; at: number; mid?: string; progress?: ProxyProgress }
const playCache = new Map<string, Play>()
async function playInfo(projectId: string, mediaId: string): Promise<Play> {
  const hit = playCache.get(mediaId)
  if (hit && (hit.status === 'ready' || hit.status === 'native') && Date.now() - hit.at < 4.5 * 3600_000) return hit
  if (hit && hit.status !== 'ready' && Date.now() - hit.at < 4_000) return hit
  const r = await api.get<{ status: string; url?: string }>(`p/${projectId}/media/${mediaId}/play`)
  const v = { ...r, at: Date.now() }
  playCache.set(mediaId, v)
  return v
}

function covering(take: Take, media: Map<string, Media>, label: string, t: number): Angle | null {
  const list = take.angles.filter((a) => a.label === label)
  return list.find((a) => t >= a.offset - 0.04 && t <= a.offset + (media.get(a.media_id)?.duration ?? 0) + 0.04) || null
}

function Slot({ projectId, angle, label, solo, register, lut, film }: { projectId: string; angle: Angle | null; label: string; solo: boolean; register: (label: string, el: HTMLVideoElement | null) => void; lut: string | null; film: boolean }) {
  const [src, setSrc] = useState<Play | null>(null)
  const mid = angle?.media_id
  // The element itself, for the look canvas; the callback stays put so React doesn't detach it each render.
  const [video, setVideo] = useState<HTMLVideoElement | null>(null)
  const registerRef = useRef(register)
  registerRef.current = register
  const setRef = useCallback((el: HTMLVideoElement | null) => {
    registerRef.current(label, el)
    setVideo(el)
  }, [label])
  const [lookFailed, setLookFailed] = useState(false)
  useEffect(() => {
    setSrc(null)
    if (!mid) return
    let live = true
    const load = async () => {
      try {
        const p = await playInfo(projectId, mid)
        if (!live) return
        setSrc({ ...p, mid })
        // While the web version is being made, check often so the bar and time left stay live.
        if (p.status !== 'ready' && p.status !== 'native') setTimeout(() => live && load(), p.status === 'pending' ? 5000 : 30000)
      } catch {
        if (live) setSrc({ status: 'error', at: Date.now() })
      }
    }
    load()
    return () => {
      live = false
    }
  }, [projectId, mid])
  const ready = src?.status === 'ready' || src?.status === 'native'
  const url = ready && src!.mid === mid ? src!.url : undefined
  // A playable URL that still has no picture after a while (a slow link, a huge original)
  // says so instead of leaving a black box.
  const [loaded, setLoaded] = useState(false)
  const [slow, setSlow] = useState(false)
  const [broken, setBroken] = useState(false)
  useEffect(() => {
    setLoaded(false)
    setSlow(false)
    setBroken(false)
    if (!url) return
    const t = setTimeout(() => setSlow(true), 15000)
    return () => clearTimeout(t)
  }, [url])
  return (
    <div className={cls('cam', solo && 'solo')}>
      {/* One element per clip: a reused element keeps the old clip's ended/readyState until
          the new URL lands, which read as "this take is over" and skipped a take. */}
      {/* crossOrigin so the look can read frames (Wasabi answers CORS; /stream is same origin). */}
      <video key={mid || 'none'} ref={setRef} src={url} crossOrigin="anonymous" playsInline preload="auto" muted data-label={label}
        onLoadedData={() => setLoaded(true)} onError={() => url && setBroken(true)} />
      {lut && url && !lookFailed && <LookCanvas video={video} lut={lut} film={film} onFail={() => setLookFailed(true)} />}
      <span className="cam-label">{label} CAM</span>
      {!angle && <div className="cam-msg">camera not rolling</div>}
      {angle && !src && <div className="cam-msg"><Spinner small /></div>}
      {angle && src && !ready && (src.status === 'pending' ? (
        <div className="cam-msg">
          <div className="cam-wait">
            <Spinner small />
            <b>preparing this video for playback</b>
            {(() => {
              const l = proxyLine(src.progress)
              return (
                <>
                  <span>{l.text}{l.pct != null ? ` · ${Math.round(l.pct * 100)}%` : ''}{l.eta ? ` · ${l.eta}` : ''}</span>
                  {l.pct != null && <div className="bar cam-bar"><div style={{ width: `${Math.max(2, l.pct * 100)}%` }} /></div>}
                  <span className="dim cam-why">The transcript works now. Video appears here on its own when it's ready.</span>
                </>
              )
            })()}
          </div>
        </div>
      ) : (
        <div className="cam-msg"><div className="cam-wait"><b>can’t play this clip</b><span className="cam-why">Let the Osmo team know and we'll take a look.</span></div></div>
      ))}
      {angle && url && broken && <div className="cam-msg"><div className="cam-wait"><b>this video didn't load</b><span className="cam-why">Try reloading the page. If it keeps happening, let the Osmo team know.</span></div></div>}
      {angle && url && !loaded && !broken && slow && <div className="cam-msg"><div className="cam-wait"><Spinner small /><b>video is taking a while to load</b><span className="cam-why">Big files can take a minute on a slow connection. The transcript works in the meantime.</span></div></div>}
    </div>
  )
}

export function MultiCam({ projectId, takes, media, playhead, apiRef, sequence, lutOf, look, canDownload }: {
  projectId: string; takes: Map<string, Take>; media: Map<string, Media>; playhead: PlayheadStore; apiRef: React.MutableRefObject<CamApi | null>
  /** The interview's takes in order: one timeline across all of them. */
  sequence?: Take[]
  /** Which look LUT a camera's clip plays through (null: as shot). */
  lutOf?: (label: string, mediaId: string | undefined) => string | null
  /** The project's look (its name goes on the picture) and, when this viewer may change it, the menu. */
  look?: LookChoice
  /** The team, or a client whose link allows downloads: the Download menu for the clips on screen. */
  canDownload?: boolean
}) {
  const [takeId, setTakeId] = useState<string | null>(null)
  const [time, setTime] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [mode, setMode] = useState<string>('all')
  const [rate, setRate] = useState(1)
  const rateRef = useRef(rate)
  rateRef.current = rate
  // Slow connections: when a camera runs out of video, the healthy ones wait for it (one clock,
  // no drift, no seek loops). Repeated stalls drop to one camera, which halves the download.
  const [buffering, setBuffering] = useState(false)
  const [notice, setNotice] = useState('')
  const gate = useRef(0) // when the current wait began, 0 when not waiting
  const lastGo = useRef(0)
  const stalls = useRef<number[]>([])
  const keepAll = useRef(false) // the viewer brought both cameras back after an automatic drop
  const els = useRef(new Map<string, HTMLVideoElement>())
  const t0 = useRef(0) // the clock while paused / before metadata
  const playingRef = useRef(false)
  const rangeEnd = useRef<number | null>(null)
  const queue = useRef<{ takeId: string; start: number; end: number }[]>([])
  const takeRef = useRef<string | null>(null)
  takeRef.current = takeId
  const take = takeId ? takes.get(takeId) : undefined
  const seq = sequence && take && sequence.some((t) => t.id === take.id) ? sequence : take ? [take] : []
  const starts: number[] = []
  let total = 0
  for (const t of seq) {
    starts.push(total)
    total += t.duration
  }
  const seqIdx = take ? seq.findIndex((t) => t.id === take.id) : -1
  const globalTime = seqIdx >= 0 ? starts[seqIdx] + time : 0
  const seqRef = useRef(seq)
  seqRef.current = seq
  const seekGlobal = (g: number, play: boolean) => {
    let k = seq.length - 1
    while (k > 0 && starts[k] > g) k--
    const tk = seq[k]
    if (!tk) return
    const first = Math.min(...tk.angles.map((a) => a.offset))
    go(tk.id, Math.max(first + 0.05, Math.min(tk.duration - 0.05, g - starts[k])), play)
  }
  const cams = take ? [...new Set(take.angles.map((a) => a.label))].sort() : []
  const shown = mode === 'all' || !cams.includes(mode) ? cams : [mode]
  const angles = Object.fromEntries(cams.map((l) => [l, take ? covering(take, media, l, time) : null]))
  const anglesRef = useRef(angles)
  anglesRef.current = angles
  const shownRef = useRef(shown)
  shownRef.current = shown

  // Keep every visible video at the right spot for take time t.
  const place = (t: number, force = false) => {
    for (const label of shownRef.current) {
      const el = els.current.get(label)
      const a = anglesRef.current[label]
      if (!el || !a || el.readyState < 1) continue
      const want = t - a.offset
      if (force) {
        el.currentTime = Math.max(0, want)
        el.playbackRate = rateRef.current
        continue
      }
      if (el.seeking) continue
      // Small drift is eased out by running a touch fast or slow; a hard seek is a visible skip
      // (and on a streamed proxy, a stall that drifts it again), so only for big jumps.
      const d = el.currentTime - want
      if (Math.abs(d) > 1) el.currentTime = Math.max(0, want)
      else if (Math.abs(d) > 0.03) el.playbackRate = rateRef.current * (1 - Math.max(-0.08, Math.min(0.08, d * 0.5)))
      else if (el.playbackRate !== rateRef.current) el.playbackRate = rateRef.current
    }
  }
  const master = () => {
    for (const label of shownRef.current) {
      const el = els.current.get(label)
      if (el && anglesRef.current[label] && el.readyState >= 1) return { label, el, offset: anglesRef.current[label]!.offset }
    }
    return null
  }
  const setAudio = () => {
    const m = master()
    els.current.forEach((el, label) => {
      el.muted = !m || label !== m.label
      el.playbackRate = rate
    })
  }
  const startAll = () => {
    setAudio()
    for (const label of shownRef.current) {
      const el = els.current.get(label)
      if (el && anglesRef.current[label]) el.play().catch(() => {})
    }
  }
  const stopAll = () => els.current.forEach((el) => el.pause())

  const go = (tid: string, t: number, play: boolean) => {
    rangeEnd.current = null
    gate.current = 0
    setBuffering(false)
    lastGo.current = performance.now()
    t0.current = t
    setTime(t)
    if (tid !== takeRef.current) {
      takeRef.current = tid
      setTakeId(tid)
    }
    playingRef.current = play
    setPlaying(play)
    playhead.set({ takeId: tid, time: t, playing: play })
    requestAnimationFrame(() => {
      place(t, true)
      play ? startAll() : stopAll()
    })
  }

  // Videos that load late (new clip, new take) catch up on metadata.
  const register = (label: string, el: HTMLVideoElement | null) => {
    if (!el) return void els.current.delete(label)
    if (els.current.get(label) === el) return
    els.current.set(label, el)
    el.onloadedmetadata = () => {
      const a = anglesRef.current[label]
      if (a) el.currentTime = Math.max(0, t0.current - a.offset)
      setAudio()
      if (playingRef.current && a) el.play().catch(() => {})
    }
  }

  // The clock: follow the master while playing, keep the rest in sync.
  useEffect(() => {
    let raf = 0
    const ahead = (el: HTMLVideoElement) => {
      const b = el.buffered
      for (let i = 0; i < b.length; i++) if (b.start(i) <= el.currentTime + 0.1 && b.end(i) > el.currentTime) return b.end(i) - el.currentTime
      return 0
    }
    const buffer = () => {
      const now = performance.now()
      if (!playingRef.current) {
        if (gate.current) {
          gate.current = 0
          setBuffering(false)
        }
        return
      }
      const live = shownRef.current
        .map((l) => els.current.get(l))
        .filter((el): el is HTMLVideoElement => !!el && !!el.currentSrc && !el.ended && !el.error)
        .filter((el) => anglesRef.current[el.dataset.label || ''])
      if (!live.length) return
      const starved = (el: HTMLVideoElement) => el.seeking || el.readyState < 3
      if (!gate.current) {
        if (!live.some(starved)) return
        gate.current = now
        // Starting or seeking always waits a beat; only count stalls mid-play.
        if (now - lastGo.current > 2000) stalls.current = [...stalls.current.filter((x) => now - x < 45_000), now]
        // Hold everything on the master's frame: other cameras pause there (one seek at most, and a
        // paused video with preload=auto keeps loading); a starved master keeps waiting in play.
        const m = master()
        live.forEach((el) => {
          if (m && el === m.el) return void (!starved(el) && el.pause())
          el.pause()
          const a = anglesRef.current[el.dataset.label || '']
          if (m && a && !el.seeking) {
            const want = m.el.currentTime + m.offset - a.offset
            if (Math.abs(el.currentTime - want) > 0.1) el.currentTime = Math.max(0, want)
          }
        })
        setTimeout(() => gate.current && setBuffering(true), 400)
        if (stalls.current.length >= 3 && shownRef.current.length > 1 && !keepAll.current) {
          if (m) {
            stalls.current = []
            setMode(m.label)
            setNotice(`Slow connection: showing ${m.label} cam only`)
          }
        }
        return
      }
      const waited = now - gate.current
      const ready = (el: HTMLVideoElement) => !el.seeking && (el.readyState === 4 || (el.readyState === 3 && (ahead(el) >= 2 || waited > 6000)))
      // Never hang: after 15 s play whatever we have.
      if (live.every(ready) || waited > 15_000) {
        gate.current = 0
        setBuffering(false)
        startAll()
      }
    }
    const loop = () => {
      buffer()
      const m = master()
      // An ended master counts as playing to its end: files often run a few frames
      // shorter than the probed duration, and an ended video pauses itself.
      if (m && playingRef.current && (!m.el.paused || m.el.ended)) {
        const t = m.el.currentTime + m.offset
        t0.current = t
        setTime((cur) => (Math.abs(cur - t) > 0.05 ? t : cur))
        playhead.set({ time: t, playing: true })
        if (!gate.current) place(t)
        if (rangeEnd.current != null && t >= rangeEnd.current) {
          const next = queue.current.shift()
          if (next) {
            go(next.takeId, next.start, true)
            rangeEnd.current = next.end
          } else {
            rangeEnd.current = null
            playingRef.current = false
            setPlaying(false)
            stopAll()
            playhead.set({ playing: false })
          }
        }
        const tk = takeRef.current ? takes.get(takeRef.current) : undefined
        const lastEnd = tk ? Math.max(...tk.angles.map((a) => a.offset + (media.get(a.media_id)?.duration ?? 0))) : 0
        if (tk && (m.el.ended || t >= Math.min(tk.duration, lastEnd) - 0.08)) {
          const list = seqRef.current
          const k = list.findIndex((x) => x.id === tk.id)
          const next = k >= 0 ? list[k + 1] : undefined
          if (next) go(next.id, Math.max(0, Math.min(...next.angles.map((a) => a.offset)) + 0.05), true)
          else {
            playingRef.current = false
            setPlaying(false)
            stopAll()
          }
        }
      }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }) // eslint-disable-line react-hooks/exhaustive-deps

  // A camera switching clips mid-take (or a solo/all change) needs a re-place.
  const angleKey = shown.map((l) => `${l}:${angles[l]?.media_id || '-'}`).join('|')
  useEffect(() => {
    requestAnimationFrame(() => {
      place(t0.current, true)
      setAudio()
      if (playingRef.current) startAll()
      else stopAll()
    })
  }, [angleKey, rate]) // eslint-disable-line react-hooks/exhaustive-deps

  apiRef.current = {
    cue: (tid, t) => ((queue.current = []), go(tid, t, false)),
    play: (tid, t) => ((queue.current = []), go(tid, t, true)),
    toggle: () => {
      if (!takeRef.current) return
      if (playingRef.current) {
        playingRef.current = false
        setPlaying(false)
        stopAll()
        playhead.set({ playing: false })
      } else {
        // Resuming keeps a line or a story going where it was.
        const end = rangeEnd.current
        go(takeRef.current, t0.current, true)
        rangeEnd.current = end
      }
    },
    pause: () => {
      playingRef.current = false
      setPlaying(false)
      stopAll()
    },
    nudge: (dt) => takeRef.current && go(takeRef.current, Math.max(0, t0.current + dt), playingRef.current),
    playRange: (tid, start, end) => {
      queue.current = []
      go(tid, start, true)
      rangeEnd.current = end
    },
    playQueue: (list) => {
      if (!list.length) return
      queue.current = list.slice(1)
      go(list[0].takeId, list[0].start, true)
      rangeEnd.current = list[0].end
    },
    current: () => ({ takeId: takeRef.current, time: t0.current, playing: playingRef.current }),
  }

  // Full screen takes the whole player (cameras plus controls), so the scrubber comes along.
  // The controls float over the picture and fade out while playing with the mouse still.
  // Filmic: Film Emulation's grain and halation on top of the look, saved with the project.
  const film = !!look?.value && !!look.film
  const stage = useRef<HTMLDivElement>(null)
  const [full, setFull] = useState(false)
  const [idle, setIdle] = useState(false)
  const idleTimer = useRef(0)
  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === stage.current)
    document.addEventListener('fullscreenchange', on)
    return () => document.removeEventListener('fullscreenchange', on)
  }, [])
  const wake = () => {
    setIdle(false)
    clearTimeout(idleTimer.current)
    idleTimer.current = window.setTimeout(() => setIdle(true), 2500)
  }
  useEffect(() => () => clearTimeout(idleTimer.current), [])
  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen?.()
    else stage.current?.requestFullscreen?.()
  }
  return (
    <div className={cls('multicam', full && idle && playing && 'fs-idle')} ref={stage} onMouseMove={full ? wake : undefined}>
      <div className={cls('cams', `n${shown.length}`)} onClick={() => apiRef.current?.toggle()} onDoubleClick={toggleFull}>
        {!take && <div className="cam-empty">click a line in the transcript, then press space to play</div>}
        {take && shown.map((label) => <Slot key={label} projectId={projectId} angle={angles[label]} label={label} solo={shown.length === 1} register={register} lut={lutOf?.(label, angles[label]?.media_id) ?? null} film={film} />)}
        {take && (
          <div className="cams-tc">
            <span>{takeTc(take, time)}</span>
            {look?.label && shown.some((l) => lutOf?.(l, angles[l]?.media_id)) ? <span className="look">{look.label}{film ? ' + filmic' : ''}</span> : <span className="temp">TEMP COLOR</span>}
          </div>
        )}
        {buffering && <div className="cams-buffer"><Spinner small /></div>}
        {notice && <div className="cams-notice">{notice}. Tap {cams.join('+')} for both.</div>}
      </div>
      <div className="controls">
        <button className="ctl-play" onClick={() => apiRef.current?.toggle()} aria-label={playing ? 'Pause' : 'Play'} disabled={!take}>
          {playing ? <PauseFillIcon /> : <PlayFillIcon />}
        </button>
        <Scrub seq={seq} starts={starts} total={total} time={globalTime} onSeek={(g) => seekGlobal(g, playingRef.current)} />
        <span className="ctl-time">{take ? `${clock(globalTime)} / ${clock(total)}` : '0:00'}</span>
        {cams.length > 1 && (
          <div className="angles" role="group" aria-label="Cameras">
            <button className={cls('angle', mode === 'all' && 'on')} onClick={() => {
              if (notice) keepAll.current = true
              setNotice('')
              setMode('all')
            }}>{cams.join('+')}</button>
            {cams.map((l) => <button key={l} className={cls('angle', mode === l && 'on')} onClick={() => (setNotice(''), setMode(l))}>{l}</button>)}
          </div>
        )}
        {look && (look.onChange || look.value) && <LookMenu look={look} />}
        {canDownload && take && <DownloadMenu projectId={projectId} clips={shown.flatMap((l) => {
          const a = angles[l]
          const m = a ? media.get(a.media_id) : undefined
          return a && m ? [{ label: l, mediaId: a.media_id, name: m.name }] : []
        })} />}
        <button className="ctl-rate" onClick={() => setRate(rate >= 2 ? 1 : rate === 1 ? 1.25 : rate === 1.25 ? 1.5 : 2)} title="Speed">{rate}x</button>
        <button className="ctl-fs" onClick={toggleFull} aria-label={full ? 'Exit full screen' : 'Full screen'}>
          {full ? <CloseIcon /> : <ExpandIcon />}
        </button>
      </div>
    </div>
  )
}

function Scrub({ seq, starts, total, time, onSeek }: { seq: Take[]; starts: number[]; total: number; time: number; onSeek: (g: number) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<number | null>(null)
  const d = total || 1
  const at = (e: React.PointerEvent) => {
    const r = ref.current!.getBoundingClientRect()
    return Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)) * d
  }
  const where = (g: number) => {
    let k = seq.length - 1
    while (k > 0 && starts[k] > g) k--
    return { k, take: seq[k], t: g - starts[k] }
  }
  const tip = hover != null && seq.length ? where(hover) : null
  return (
    <div
      className="scrub"
      ref={ref}
      onPointerDown={(e) => {
        if (!seq.length) return
        ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
        onSeek(at(e))
      }}
      onPointerMove={(e) => {
        if (!seq.length) return
        setHover(at(e))
        if (e.buttons === 1) onSeek(at(e))
      }}
      onPointerLeave={() => setHover(null)}
    >
      <div className="scrub-track"><div className="scrub-fill" style={{ width: `${(time / d) * 100}%` }} /></div>
      {seq.length > 1 && starts.map((s0, i) => (
        <div key={i} className={'scrub-take' + (i ? '' : ' first')} style={{ left: `${(s0 / d) * 100}%` }}>
          <span>Take {i + 1}</span>
        </div>
      ))}
      {tip && <div className="scrub-tip" style={{ left: `${(hover! / d) * 100}%` }}>{seq.length > 1 ? `Take ${tip.k + 1} · ` : ''}{takeTc(tip.take, tip.t)}</div>}
    </div>
  )
}
